import { NavLink } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useT } from "@/lib/i18n";
import {
  Wand2, Activity, ShieldCheck, Cpu, Mic, UserCheck, GitMerge,
  DollarSign, Sparkles, Share2, Lock, CreditCard, UserCircle, Radio, Bot, Library, Clapperboard,
} from "lucide-react";

const groups = [
  {
    title: "Core Studios",
    items: [
      { name: "Unified Studio", path: "/studio", icon: Wand2, badge: "Merged" },
      { name: "Executive AI", path: "/agent", icon: Bot, badge: "Agent" },
      { name: "Asset Library", path: "/assets", icon: Library, badge: "Real" },
      { name: "Faceless Factory", path: "/faceless", icon: Radio, badge: "New" },
      { name: "Operations Center", path: "/operations", icon: Activity, badge: "Live" },
      { name: "Admin Panel", path: "/admin", icon: ShieldCheck, badge: "RBAC" },
    ],
  },
  {
    title: "Cinematic Arsenal",
    items: [
      { name: "Engine Manager", path: "/engines", icon: Cpu, badge: "Live" },
      { name: "Voice & Dubbing", path: "/voice-dubbing", icon: Mic, badge: "HD" },
      { name: "Avatar Studio", path: "/avatars", icon: UserCheck, badge: "Live" },
      { name: "Render Studio", path: "/renders", icon: Clapperboard, badge: "GPU" },
      { name: "Workflow Studio", path: "/workflows", icon: GitMerge, badge: "Soon" },
    ],
  },
  {
    title: "Governance & Distribution",
    items: [
      { name: "Cost Governor", path: "/cost-governor", icon: DollarSign, badge: "$" },
      { name: "Prepaid Packs", path: "/billing", icon: CreditCard, badge: "UPI" },
      { name: "My Account", path: "/account", icon: UserCircle, badge: "Me" },
      { name: "Quality Studio", path: "/quality", icon: Sparkles, badge: "Soon" },
      { name: "Distribution Hub", path: "/distribution", icon: Share2, badge: "Multi" },
      { name: "Security Center", path: "/security", icon: Lock, badge: "RBAC" },
    ],
  },
];

import { useHealth } from "@/lib/useHealth";

export default function Sidebar() {
  const health = useHealth();
  const { t } = useT();
  return (
    <aside
      data-testid="app-sidebar"
      className="hidden md:flex flex-col w-64 lg:w-72 shrink-0 h-screen sticky top-0 bg-[#0A0C14]/90 backdrop-blur-xl border-r border-white/10 grain"
    >
      <div className="px-6 h-16 flex items-center border-b border-white/10">
        <Logo size={34} />
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-6">
        {groups.map((g) => (
          <div key={g.title}>
            <div className="px-3 mb-2 text-[10px] font-mono uppercase tracking-widest text-slate-600">
              {t(g.title)}
            </div>
            <div className="space-y-1">
              {g.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.path.replace("/", "")}`}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                      isActive
                        ? "bg-gradient-to-r from-purple-600/20 to-cyan-500/10 text-white border border-purple-500/30"
                        : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
                    }`
                  }
                >
                  <item.icon size={17} className="shrink-0" />
                  <span className="flex-1 truncate">{t(item.name)}</span>
                  <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-md bg-white/5 text-slate-500 group-hover:text-cyan-400">
                    {item.badge}
                  </span>
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="p-4 border-t border-white/10">
        <div data-testid="sidebar-health" className="glass rounded-xl p-3 text-[11px] text-slate-400">
          <div className={`font-mono ${health?.status === "ok" ? "text-emerald-400" : "text-amber-400"}`}>{health ? (health.status === "ok" ? "All systems live" : health.status === "unreachable" ? "API unreachable" : "Degraded") : "Checking…"}</div>
          {health?.renders_24h && <div className="mt-1">{health.renders_24h.completed} renders today · {health.renders_24h.rendering} in progress{health.renders_24h.failed ? ` · ${health.renders_24h.failed} failed` : ""}</div>}
          {health?.providers && <div className="mt-0.5 text-slate-500">{health.providers.video.length} video engines · YouTube {health.providers.youtube_oauth === "CONFIGURED" ? "ready" : "not linked"}</div>}
        </div>
      </div>
    </aside>
  );
}
